import React, { useState } from 'react';
import { useLearningStats, useTodayProgress, useUpdateDailyGoal } from '../api/learningStatsApi';

const LearningDashboard: React.FC = () => {
  const { data: stats, isLoading: statsLoading } = useLearningStats();
  const { data: todayProgress, isLoading: progressLoading } = useTodayProgress();
  const updateDailyGoalMutation = useUpdateDailyGoal();
  
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [newGoal, setNewGoal] = useState(stats?.dailyGoal || 3);

  const handleGoalUpdate = () => {
    updateDailyGoalMutation.mutate(newGoal);
    setIsEditingGoal(false);
  };

  if (statsLoading || progressLoading) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 animate-pulse">
        <div className="h-6 bg-gray-300 rounded mb-4 w-1/3"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg p-4">
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-8 bg-gray-300 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!stats || !todayProgress) return null;

  const getStreakEmoji = (streak: number) => {
    if (streak === 0) return '😴';
    if (streak < 3) return '🌱';
    if (streak < 7) return '🔥';
    if (streak < 14) return '⚡';
    if (streak < 30) return '🚀';
    return '👑';
  };

  const getMotivationalMessage = () => {
    const { progress, goal } = todayProgress;
    
    if (progress >= goal) {
      return "🎉 Daily goal achieved! You're on fire!";
    }
    if (progress === 0) {
      return "💪 Ready to start learning today?";
    }
    const remaining = goal - progress;
    return `⚡ ${remaining} more lecture${remaining > 1 ? 's' : ''} to reach your goal!`;
  };

  const getDailyGoalColor = () => {
    const percentage = todayProgress.percentage;
    if (percentage >= 100) return 'from-green-400 to-green-600';
    if (percentage >= 75) return 'from-yellow-400 to-orange-500';
    if (percentage >= 50) return 'from-blue-400 to-blue-600';
    return 'from-gray-400 to-gray-500';
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Learning Dashboard</h2>
        <div className="text-sm text-gray-600">
          {getMotivationalMessage()}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Daily Goal Progress */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-gray-800">Today's Goal</h3>
            {!isEditingGoal && (
              <button
                onClick={() => {
                  setNewGoal(stats.dailyGoal);
                  setIsEditingGoal(true);
                }}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Edit
              </button>
            )}
          </div>
          
          {isEditingGoal ? (
            <div className="space-y-3">
              <input
                type="number"
                min="1"
                max="20"
                value={newGoal}
                onChange={(e) => setNewGoal(parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleGoalUpdate}
                  disabled={updateDailyGoalMutation.isPending}
                  className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => setIsEditingGoal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-3 rounded-md text-sm hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {todayProgress.progress}/{todayProgress.goal}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full bg-gradient-to-r ${getDailyGoalColor()} transition-all duration-500`}
                  style={{ width: `${Math.min(100, todayProgress.percentage)}%` }}
                ></div>
              </div>
              <div className="text-sm text-gray-600">
                {todayProgress.percentage}% complete
              </div>
            </>
          )}
        </div>

        {/* Current Streak */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Current Streak</h3>
          <div className="flex items-center space-x-3">
            <div className="text-4xl">
              {getStreakEmoji(stats.currentStreak)}
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">
                {stats.currentStreak}
              </div>
              <div className="text-sm text-gray-600">
                {stats.currentStreak === 1 ? 'day' : 'days'}
              </div>
            </div>
          </div>
          {stats.currentStreak > 0 && (
            <div className="mt-3 text-sm text-gray-600">
              Don't break the chain! 🔗
            </div>
          )}
        </div>

        {/* Learning Stats */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Your Stats</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Best Streak:</span>
              <span className="font-semibold text-gray-900">
                {stats.bestStreak} days 🏆
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Lectures:</span>
              <span className="font-semibold text-gray-900">
                {stats.totalLecturesWatched} 📚
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Learning Days:</span>
              <span className="font-semibold text-gray-900">
                {stats.learningDays.length} 📅
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Achievement Celebration */}
      {todayProgress.percentage >= 100 && (
        <div className="mt-6 bg-gradient-to-r from-green-400 to-green-600 text-white rounded-lg p-4 text-center">
          <div className="text-2xl mb-2">🎉 🎊 🎉</div>
          <div className="text-lg font-semibold">
            Awesome! You've hit your daily goal!
          </div>
          <div className="text-sm opacity-90 mt-1">
            Keep this momentum going tomorrow! 💪
          </div>
        </div>
      )}

      {/* New Streak Achievement */}
      {stats.currentStreak > stats.bestStreak - 1 && stats.currentStreak >= 3 && (
        <div className="mt-6 bg-gradient-to-r from-purple-400 to-purple-600 text-white rounded-lg p-4 text-center">
          <div className="text-2xl mb-2">🔥 ⚡ 🔥</div>
          <div className="text-lg font-semibold">
            {stats.currentStreak}-Day Streak! You're unstoppable!
          </div>
          <div className="text-sm opacity-90 mt-1">
            Learning streaks build powerful habits! 🧠
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningDashboard; 